import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { HeroVisual } from "@/components/HeroVisual";
import { MethodVisual, BusinessPlanVisual } from "@/components/LandingVisuals";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  FileInput, Layers, Waves, PieChart, BrainCircuit, FileText, ArrowRight,
  Cpu, Gauge, TrendingUp, Clock, ShieldCheck, Target, CheckCircle2, HandCoins,
  Zap, Activity, ListChecks, GitBranch,
} from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/67301/abstract-architecture-background-blue-67301.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Landing() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const go = () => navigate(user ? "/analysis/new" : "/register");

  const features = [
    { icon: FileInput, t: "f_ingest_t", d: "f_ingest_d" },
    { icon: Layers, t: "f_reclass_t", d: "f_reclass_d" },
    { icon: Waves, t: "f_cash_t", d: "f_cash_d" },
    { icon: PieChart, t: "f_ratios_t", d: "f_ratios_d" },
    { icon: BrainCircuit, t: "f_rating_t", d: "f_rating_d" },
    { icon: FileText, t: "f_comment_t", d: "f_comment_d" },
  ];

  return (
    <div className="App bg-slate-900">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden grain bg-slate-950">
        <div className="absolute inset-0" aria-hidden="true">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-[0.06] mix-blend-luminosity" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/40 via-slate-950/70 to-slate-950" />
          <div className="absolute -top-32 right-0 h-[32rem] w-[32rem] rounded-full bg-cyan-400/10 blur-3xl" />
          <div className="absolute -bottom-40 -left-20 h-[28rem] w-[28rem] rounded-full bg-blue-600/10 blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-20 lg:pt-24 lg:pb-28">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="max-w-2xl animate-fade-up">
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-800 bg-slate-900 px-3 py-1.5 text-xs font-semibold tracking-wide text-slate-300 mb-7">
                <BrainCircuit className="h-3.5 w-3.5 text-blue-600" /> {t("hero_badge")}
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
                {t("hero_title")}
              </h1>
              <p className="mt-6 text-lg text-slate-300 max-w-xl leading-relaxed">{t("hero_sub")}</p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Button size="lg" data-testid="hero-cta-btn" onClick={go} className="group h-12 px-7 text-base">
                  {t("hero_cta")}
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Button>
                <a href="#method" className="text-sm font-semibold text-slate-200 hover:text-blue-600 transition-colors duration-200"
                  data-testid="hero-method-link">
                  {t("hero_cta2")} →
                </a>
              </div>
              <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-2">
                {["hero_trust1", "hero_trust2", "hero_trust3"].map((k) => (
                  <span key={k} className="inline-flex items-center gap-2 text-xs font-medium text-slate-400">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" /> {t(k)}
                  </span>
                ))}
              </div>
            </div>

            <HeroVisual />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 py-20 lg:py-28">
        <div className="max-w-2xl">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600">Rexxpons · Moduli</p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white">{t("features_title")}</h2>
          <p className="mt-4 text-slate-300">{t("features_sub")}</p>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <div key={f.t} data-testid={`feature-card-${i}`}
              className="group rounded-xl border border-slate-800 bg-slate-900 p-7 hover:border-blue-300 hover:shadow-lg transition-[box-shadow,border-color] duration-200">
              <div className="h-11 w-11 rounded-lg bg-slate-900 flex items-center justify-center text-white group-hover:bg-blue-600 transition-colors duration-200">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-5 font-heading text-lg font-semibold text-white">{t(f.t)}</h3>
              <p className="mt-2 text-sm text-slate-300 leading-relaxed">{t(f.d)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Intro: beyond accounting */}
      <section className="bg-slate-900 border-y border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 lg:py-28">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600">{t("mk_intro_eye")}</p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white">{t("mk_intro_title")}</h2>
            <p className="mt-4 text-lg text-slate-300 leading-relaxed">{t("mk_intro_body")}</p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-4">
            {[
              { k: "mk_s1", icon: GitBranch, span: "md:col-span-7", tone: "accent" },
              { k: "mk_s2", icon: Activity, span: "md:col-span-5", tone: "plain" },
              { k: "mk_s3", icon: BrainCircuit, span: "md:col-span-5", tone: "plain" },
              { k: "mk_s4", icon: ListChecks, span: "md:col-span-7", tone: "dark" },
            ].map((s, i) => (
              <div key={s.k} data-testid={`strength-${i}`}
                className={`group ${s.span} rounded-2xl p-6 sm:p-7 border transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:shadow-lg
                  ${s.tone === "dark" ? "bg-slate-900 border-slate-900 text-white"
                    : s.tone === "accent" ? "bg-gradient-to-br from-blue-950 to-cyan-50/40 border-blue-100"
                    : "bg-slate-900 border-slate-800"}`}>
                <div className={`inline-flex h-11 w-11 items-center justify-center rounded-xl mb-4
                  ${s.tone === "dark" ? "bg-white/10 text-cyan-300" : "bg-blue-600 text-white"}`}>
                  <s.icon className="h-5 w-5" />
                </div>
                <p className={`font-heading text-base sm:text-lg font-semibold leading-snug ${s.tone === "dark" ? "text-white" : "text-white"}`}>
                  {t(s.k)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="max-w-7xl mx-auto px-4 sm:px-6 py-20 lg:py-28">
        <div className="max-w-2xl">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600">{t("mk_tech_title")}</p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white">{t("mk_tech_sub")}</h2>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[{ icon: Cpu, t: "mk_tech_1_t", d: "mk_tech_1_d" },
            { icon: Gauge, t: "mk_tech_2_t", d: "mk_tech_2_d" },
            { icon: TrendingUp, t: "mk_tech_3_t", d: "mk_tech_3_d" }].map((s, i) => (
            <div key={s.t} data-testid={`step-${i}`} className="rounded-xl border border-slate-800 bg-slate-900 p-7">
              <div className="h-11 w-11 rounded-lg bg-blue-600 flex items-center justify-center text-white"><s.icon className="h-5 w-5" /></div>
              <h3 className="mt-5 font-heading text-lg font-semibold text-white">{t(s.t)}</h3>
              <p className="mt-2 text-sm text-slate-300 leading-relaxed">{t(s.d)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Advantages — predictive edge showcase */}
      <section className="relative overflow-hidden bg-slate-950 grain">
        <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-blue-600/20 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-cyan-500/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 lg:py-28">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-400">{t("adv_eye")}</p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white">{t("adv_head")}</h2>
            <p className="mt-4 text-lg text-slate-500 leading-relaxed">{t("adv_sub")}</p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[{ icon: Zap, stat: "adv_speed_stat", t: "adv_speed_t", d: "adv_speed_d", accent: "from-blue-500 to-cyan-400" },
              { icon: Activity, stat: "adv_mc_stat", t: "adv_mc_t", d: "adv_mc_d", accent: "from-violet-500 to-blue-500" },
              { icon: ListChecks, stat: "adv_reco_stat", t: "adv_reco_t", d: "adv_reco_d", accent: "from-emerald-500 to-teal-400" }].map((a, i) => (
              <div key={a.t} data-testid={`advantage-${i}`}
                className="group relative rounded-2xl border border-white/10 bg-slate-900/[0.03] p-7 backdrop-blur-sm hover:border-white/20 hover:bg-slate-800/[0.06] transition-[background-color,border-color,transform] duration-300 hover:-translate-y-1">
                <div className={`inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${a.accent} text-white shadow-lg`}>
                  <a.icon className="h-6 w-6" />
                </div>
                <div className="mt-6 font-heading text-4xl font-extrabold tracking-tight bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent tabular-nums">
                  {t(a.stat)}
                </div>
                <h3 className="mt-3 font-heading text-lg font-semibold text-white">{t(a.t)}</h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{t(a.d)}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[{ icon: Clock, t: "mk_adv_1_t", d: "mk_adv_1_d" },
              { icon: ShieldCheck, t: "mk_adv_2_t", d: "mk_adv_2_d" },
              { icon: FileText, t: "mk_adv_3_t", d: "mk_adv_3_d" },
              { icon: Target, t: "mk_adv_4_t", d: "mk_adv_4_d" }].map((a, i) => (
              <div key={a.t} data-testid={`advantage-mini-${i}`}
                className="flex items-start gap-3 rounded-xl border border-white/10 bg-slate-900/[0.02] p-5">
                <a.icon className="h-5 w-5 text-blue-400 mt-0.5 shrink-0" />
                <div>
                  <h4 className="font-heading text-sm font-semibold text-white">{t(a.t)}</h4>
                  <p className="mt-1 text-xs text-slate-500 leading-relaxed">{t(a.d)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Method */}
      <section id="method" className="bg-slate-900 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-400">{t("nav_method")}</p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white">{t("method_title")}</h2>
            <p className="mt-6 text-slate-300 leading-relaxed">{t("method_body")}</p>
          </div>
          <MethodVisual />
        </div>
      </section>

      {/* Business Plan — integrated module */}
      <section id="business-plan" className="bg-slate-900 border-y border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-400">{t("bl_eye")}</p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white">{t("bl_title")}</h2>
            <p className="mt-4 text-lg text-slate-300 leading-relaxed">{t("bl_body")}</p>
            <ul className="mt-8 space-y-3">
              {["bl_f1", "bl_f2", "bl_f3", "bl_f4"].map((k) => (
                <li key={k} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" />
                  <span className="text-sm text-slate-200">{t(k)}</span>
                </li>
              ))}
            </ul>
            <Button size="lg" className="mt-9 h-12 px-7 group" data-testid="bl-cta-btn"
              onClick={() => navigate(user ? "/business-plan/new" : "/register")}>
              {t("bl_cta")}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Button>
          </div>
          <BusinessPlanVisual />
        </div>
      </section>

      {/* Working capital advisory */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 lg:py-24">
        <div className="rounded-3xl bg-slate-900 border border-slate-800 p-8 sm:p-12 flex flex-col lg:flex-row lg:items-center gap-8">
          <div className="h-16 w-16 rounded-2xl bg-blue-600 flex items-center justify-center text-white shrink-0">
            <HandCoins className="h-8 w-8" />
          </div>
          <div className="flex-1">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white">{t("mk_circ_title")}</h2>
            <p className="mt-3 text-slate-300 leading-relaxed max-w-2xl">{t("mk_circ_body")}</p>
          </div>
          <Button size="lg" className="h-12 px-7 shrink-0" data-testid="circ-cta-btn"
            onClick={() => navigate("/pricing")}>{t("mk_circ_cta")}</Button>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="bg-slate-900 relative overflow-hidden grain">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white">{t("pr_title")}</h2>
          <p className="mt-4 text-slate-300 max-w-xl mx-auto">{t("pr_sub")}</p>
          <Button size="lg" className="mt-8 h-12 px-8" data-testid="pricing-cta-btn"
            onClick={() => navigate("/pricing")}>{t("nav_pricing")} <ArrowRight className="ml-2 h-4 w-4" /></Button>
        </div>
      </section>
    </div>
  );
}
